import { supabase } from '@/integrations/supabase/client';
import { BlogPost, BlogPostCreateInput, BlogPostUpdateInput, transformBlogPost } from '../types';

export const createPost = async (input: BlogPostCreateInput): Promise<BlogPost> => {
  const user = (await supabase.auth.getUser()).data.user;
  
  // Prepare post data
  const postData = {
    id: crypto.randomUUID(),
    title: input.title,
    slug: input.slug,
    summary: input.summary,
    content: input.content,
    featured_image: input.featured_image,
    tags: input.tags || [],
    published: input.published || false,
    published_at: input.published ? new Date().toISOString() : null,
    meta_title: input.meta_title || input.title,
    meta_description: input.meta_description || input.summary,
    view_count: 0,
    author_id: user?.id,
    author_name: input.author_name || 'The Mat Monster Team',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const { data, error } = await supabase
    .from('blog_posts')
    .insert(postData)
    .select()
    .single();

  if (error) throw error;
  return transformBlogPost(data);
};

export const updatePost = async (id: string, input: BlogPostUpdateInput): Promise<BlogPost> => {
  // Check if publication status changed to true
  const updateData: BlogPostUpdateInput = {
    ...input,
    updated_at: new Date().toISOString()
  };

  if (input.published && input.publish_status_changed) {
    updateData.published_at = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('blog_posts')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return transformBlogPost(data);
};

export const deletePost = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('blog_posts')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

export const duplicatePost = async (id: string): Promise<BlogPost> => {
  // First fetch the post to duplicate
  const { data: originalPost, error: fetchError } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  // Create a new post based on the original
  const newPost = {
    ...originalPost,
    id: crypto.randomUUID(),
    title: `${originalPost.title} (Copy)`,
    slug: `${originalPost.slug}-copy-${Date.now().toString().slice(-6)}`,
    published: false,
    published_at: null,
    view_count: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };

  const { data, error } = await supabase
    .from('blog_posts')
    .insert(newPost)
    .select()
    .single();

  if (error) throw error;
  return transformBlogPost(data);
};
